import { useState } from "react";
import { useCookies } from "react-cookie";
import { useToken } from "@app/hooks/useToken";
import { useMsal } from "@azure/msal-react";
import { StorageKey } from "@app/constants";
import { useForceLogout } from "./useForceLogout";
import { msalConfig } from "@app/api";
import { setItem } from "@app/helpers/storage.helper";

let logoutRedirectPending = false;
export const isLogoutRedirectPending = () => logoutRedirectPending;

export const useAuth = () => {
  const { saveTokens, getTokens, clearTokens } = useToken();
  const { clearTimer, setTimer } = useForceLogout();
  const [isLogged, setIsLogged] = useState(getTokens().accessToken);
  const { instance } = useMsal();
  const [cookies] = useCookies([
    StorageKey.AUTH_ACCESS_TOKEN,
    StorageKey.LAST_UPDATED_AT,
  ]);

  const login = (data: any) => {
    const { idToken, account } = data;
    saveTokens({
      token: idToken,
      accountId: account.homeAccountId,
    });
    setIsLogged(idToken);

    setItem(StorageKey.IS_SHOWN_DATA_USAGE_POPUP, true);

    setTimer();
  };

  const clearAllStorage = () => {
    try {
      sessionStorage.clear();

      const allKeys = Object.keys(localStorage);
      allKeys.forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error("Error clearing storage:", error);
    }

    try {
      const cookiesList = document.cookie.split(";");
      cookiesList.forEach((cookie) => {
        const eqPos = cookie.indexOf("=");
        const name =
          eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
        if (name) {
          const hostname = window.location.hostname;
          const paths = ["/", window.location.pathname];
          const domains = [
            hostname,
            `.${hostname}`,
            ...(hostname.includes(".")
              ? [
                  hostname.split(".").slice(-2).join("."),
                  `.${hostname.split(".").slice(-2).join(".")}`,
                ]
              : []),
            "localhost",
          ];

          paths.forEach((path) => {
            domains.forEach((domain) => {
              document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`;
              document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain}`;
            });
          });
        }
      });
    } catch (error) {
      console.error("Error clearing cookies:", error);
    }

    try {
      clearTokens();
    } catch (error) {
      console.error("Error clearing tokens:", error);
    }
  };

  const logout = async (logOutHead?: boolean) => {
    logoutRedirectPending = true;

    let account = null;
    let accountId: string | undefined = undefined;

    try {
      accountId = cookies.tokens?.accountId;

      account = accountId
        ? instance.getAccountByHomeId(accountId)
        : instance.getAllAccounts()[0] || null;
    } catch (error) {
      console.error("Error getting account for logout:", error);

      try {
        account = instance.getAllAccounts()[0] || null;
      } catch (fallbackError) {
        console.error("Error getting account fallback:", fallbackError);
      }
    }

    let logoutRedirectUri: string;

    if (logOutHead && msalConfig.auth.redirectUri) {
      try {
        const redirectUrl = new URL(msalConfig.auth.redirectUri);
        logoutRedirectUri = `${redirectUrl.origin}/login`;
      } catch {
        logoutRedirectUri = `${window.location.origin}/login`;
      }
    } else {
      logoutRedirectUri = `${window.location.origin}/login`;
    }

    setIsLogged("");
    clearTimer();

    try {
      await instance.logoutRedirect({
        account: account || undefined,
        postLogoutRedirectUri: logoutRedirectUri,
        onRedirectNavigate: () => {
          clearAllStorage();
          return true;
        },
      });

      setTimeout(() => {
        clearAllStorage();
      }, 100);
    } catch (error: any) {
      console.error("Logout error:", error);

      clearAllStorage();
      window.location.href = "/login";
    }
  };

  return { login, logout, isLogged };
};
